import { Card, CardContent } from '@/ui/design-system/components';
import { DrawerStack } from './styledComponents';
import { BodySemi } from 'components/Text';
import DrawerBand from 'components/DrawerBand';
import { useContext, useRef } from 'react';
import {
    useCurrentCountry,
    useEndDate,
    useTikTokData,
    useTikTokWeeks,
} from 'utils/hooks';
import { CreatorsTable } from 'components/Creators';
import DimensionIcon from 'components/DimensionIcon';
import DrawerHeader from 'components/DrawerHeader';
import DownloadTimeSeriesButton from 'components/Button/DownloadTimeSeriesButton';
import { buildExportFilename } from 'utils/formatCSV';
import ErrorBoundary from 'containers/ErrorBoundary';
import Stack from 'components/Stack';
import { ThemeContext } from 'styled-components';
import TikTokHashtagTimeSeries from 'components/DataVis/Charts/TikTokHashtagTimeSeries/TikTokHashtagTimeSeries';
import TikTokTimeSeriesNoDataMessage from 'components/TikTokTimeSeriesNoDataMessage';
import ToggleWatch from 'components/Button/ToggleWatch';
import { TopPostsHashtagDrawer } from 'components/Posts';
import bugsnagClient from 'containers/BugsnagClient';
import { getTikTokData } from 'utils/api/tiktok.api';
import { parseTikTokTimeSeriesQuery } from 'utils/helpers';
import { useQuery } from 'react-query';
import { v4 as uuid } from 'uuid';

const queryFn = ({ queryKey }) =>
    getTikTokData({
        data: { ...queryKey[1] },
    });

function TikTokDrawerContentIndividualHashtag({
    hashtag,
    initialData,
    category,
    brand,
    showAddToWatchlist = true,
}) {
    const theme = useContext(ThemeContext);
    const country = useCurrentCountry();
    const timeSeriesRef = useRef();
    const { week, year } = useTikTokWeeks();
    const date = useEndDate('tiktok');
    const {
        data: hashtagData,
        isLoading: isLoadingHashtagData,
        firstPageQuery,
    } = useTikTokData({
        paginateBy: 'hashtags',
        hashtags: [hashtag],
        countries: country ? [country.id] : null,
        categories: category ? [category] : null,
        brands: brand === '*' ? '*' : brand ? [brand] : null,
        week,
        year,
        enabled: !!(hashtag && country && week && year),
        fetchAllPages: false,
        per_page: 1,
        placeholderData: initialData ? [initialData] : undefined,
    });

    const dataPoint = hashtagData?.[0];
    const name = dataPoint?.name || hashtag;
    const hashtagId = dataPoint?.hashtag;

    const tsQuery = useQuery({
        queryKey: [
            'TikTokData',
            {
                country: country ? [country.id] : null,
                hashtag: hashtagId ? [hashtagId] : null,
                week: null,
                year: null,
            },
        ],
        queryFn,
        enabled: !!(hashtagId && country),
    });

    const tsId = tsQuery.data?.data?.[0]?.id || null;
    const timeSeries = parseTikTokTimeSeriesQuery(tsQuery, bugsnagClient);
    const timeSeriesName = `${name} Views (${country?.code || country?.name})`;
    const timeSeriesCsvFilename = buildExportFilename({
        entity: name,
        country,
        source: 'tiktok',
        type: 'hashtag',
        kind: 'time-series',
        date,
    });

    const tsData = [
        {
            id: tsId,
            name: `TikTok${country ? ` (${country.code || country.name})` : null}`,
            timeSeries,
            type: 'views',
            color: theme.tiktok_pink,
            metricLabel: 'views',
        },
    ];

    if (isLoadingHashtagData) {
        return (
            <Stack $space="1.5rem" css="padding-bottom: 2.25rem;">
                <DrawerHeader name={hashtag} capitalize={false} />
                <div
                    css={`
                        text-align: center;
                        padding: 2rem;
                        color: ${theme.gray_600};
                    `}
                >
                    Loading hashtag data...
                </div>
            </Stack>
        );
    }

    if (firstPageQuery.isError) {
        return (
            <Stack $space="1.5rem" css="padding-bottom: 2.25rem;">
                <DrawerHeader name={hashtag} capitalize={false} />
                <div
                    css={`
                        text-align: center;
                        padding: 2rem;
                        color: ${theme.red_600};
                    `}
                >
                    Error loading hashtag data. Please try again.
                </div>
            </Stack>
        );
    }

    if (!dataPoint) {
        return (
            <Stack $space="1.5rem" css="padding-bottom: 2.25rem;">
                <DrawerHeader name={hashtag} capitalize={false} />
                <div
                    css={`
                        text-align: center;
                        padding: 2rem;
                        color: ${theme.gray_600};
                    `}
                >
                    Hashtag not found or no data available for this week.
                </div>
            </Stack>
        );
    }

    return (
        <Stack $space="1.5rem" css="padding-bottom: 2.25rem;">
            <DrawerHeader
                name={name}
                capitalize={false}
                eyebrow={
                    <>
                        <DimensionIcon dimensionType="hashtag" />
                        hashtag
                    </>
                }
            >
                {showAddToWatchlist && (
                    <ToggleWatch
                        id={uuid()}
                        data={{
                            name,
                            type: 'hashtag',
                            [dataPoint?.brand ? 'brandId' : 'categoryId']:
                                dataPoint?.brand || dataPoint?.category,
                        }}
                        btnChildren="Add to watchlist"
                    />
                )}
            </DrawerHeader>
            <DrawerBand>
                <Card>
                    <CardContent className="p-4 flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                            <BodySemi as="span">Views</BodySemi>
                            <DownloadTimeSeriesButton
                                menuAlign="right"
                                csvFilename={timeSeriesCsvFilename}
                                imageTitle={timeSeriesName}
                                data={tsData}
                                wrapperRef={timeSeriesRef}
                            />
                        </div>
                        <ErrorBoundary>
                            <TikTokHashtagTimeSeries
                                chartHeight={287}
                                data={tsData}
                                isLoading={tsQuery.isLoading}
                                showPlaceholder={
                                    tsQuery.isLoading || !timeSeries
                                }
                                placeholderMessage={
                                    !(timeSeries && timeSeries.length > 0) &&
                                    tsQuery.isLoading ? (
                                        'Loading...'
                                    ) : (
                                        <TikTokTimeSeriesNoDataMessage />
                                    )
                                }
                                wrapperRef={timeSeriesRef}
                            />
                        </ErrorBoundary>
                    </CardContent>
                </Card>
            </DrawerBand>
            <DrawerStack>
                <TopPostsHashtagDrawer
                    week={week}
                    year={year}
                    countries={country ? [country.id] : null}
                    hashtags={hashtagId ? [hashtagId] : null}
                    name={name}
                    displayQty={6}
                />
            </DrawerStack>
            <DrawerStack>
                <CreatorsTable
                    year={year}
                    week={week}
                    hashtag={hashtagId}
                    country={country?.id || null}
                    filename={buildExportFilename({
                        entity: name || 'trend',
                        country,
                        source: 'tiktok',
                        type: 'creator',
                        kind: 'data',
                        date,
                    })}
                />
            </DrawerStack>
        </Stack>
    );
}

export default TikTokDrawerContentIndividualHashtag;
